import React from 'react';
import styled from 'styled-components';

import { sermons } from '../sermons_data';

const SermonsContainer = styled.div`
  min-height: 100vh;
`;

const HeroSection = styled.section`
  background: linear-gradient(135deg, #2c5aa0 0%, #1e3a8a 100%);
  color: #fff;
  padding: 80px 0;
  text-align: center;

  @media (max-width: 768px) {
    padding: 60px 0;
  }
`;

const HeroContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;

  @media (max-width: 768px) {
    padding: 0 15px;
  }
`;

const Title = styled.h1`
  font-size: 48px;
  font-weight: 700;
  margin-bottom: 20px;

  @media (max-width: 768px) {
    font-size: 32px;
  }
`;

const Subtitle = styled.p`
  font-size: 20px;
  opacity: 0.9;
  max-width: 600px;
  margin: 0 auto;

  @media (max-width: 768px) {
    font-size: 16px;
  }
`;



const SermonsGrid = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 40px 20px;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  gap: 40px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 30px;
    padding: 20px;
  }
`;

const SermonCard = styled.div`
  background: #fff;
  border-radius: 8px;
  overflow: hidden;
  cursor: pointer;
  transition: transform 0.2s ease;

  &:hover {
    transform: translateY(-5px);
  }
`;

const ThumbnailWrapper = styled.div`
  position: relative;
  padding-top: 56.25%; /* 16:9 Aspect Ratio */
  background: #000;
  overflow: hidden;
  border-radius: 8px;

  img {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  &::after {
    content: '▶';
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 60px;
    height: 60px;
    background: rgba(0, 0, 0, 0.6);
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
    font-size: 24px;
    opacity: 0;
    transition: opacity 0.2s ease;
  }

  ${SermonCard}:hover &::after {
    opacity: 1;
  }
`;

const CardContent = styled.div`
  padding: 15px 0;
  text-align: center;
`;

const SermonTitle = styled.div`
  font-size: 14px;
  color: #333;
  margin-bottom: 8px;
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  height: 40px;
`;

const SermonDate = styled.div`
  font-size: 16px;
  color: #333;
  font-weight: 500;
`;

const PaginationContainer = styled.div`
  display: flex;
  justify-content: center;
  gap: 10px;
  margin-top: 40px;
  padding-bottom: 40px;
`;

const PageButton = styled.button<{ active?: boolean }>`
  padding: 8px 16px;
  border: 1px solid #ddd;
  background: ${props => props.active ? '#2c5aa0' : '#fff'};
  color: ${props => props.active ? '#fff' : '#333'};
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: ${props => props.active ? '#2c5aa0' : '#f5f5f5'};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;



const Sermons: React.FC = () => {
  const [selectedVideo, setSelectedVideo] = React.useState<string | null>(null);

  // Pagination
  const [currentPage, setCurrentPage] = React.useState(1);
  const itemsPerPage = 9;
  const totalPages = Math.ceil(sermons.length / itemsPerPage);

  const currentSermons = sermons.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const handlePlayClick = (videoId: string) => {
    setSelectedVideo(videoId);
  };

  const closeVideo = () => {
    setSelectedVideo(null);
  };

  return (
    <SermonsContainer>
      <HeroSection>
        <HeroContent>
          <Title>주일 설교</Title>
          <Subtitle>하나님의 말씀을 통해 한 주간의 삶에 힘을 얻으시기 바랍니다</Subtitle>
        </HeroContent>
      </HeroSection>

      <SermonsGrid>
        {currentSermons.map((sermon, index) => (
          <SermonCard key={index} onClick={() => handlePlayClick(sermon.videoId)}>
            <ThumbnailWrapper>
              <img
                src={`https://img.youtube.com/vi/${sermon.videoId}/hqdefault.jpg`}
                alt={sermon.date}
              />
            </ThumbnailWrapper>
            <CardContent>
              <SermonTitle>{sermon.title}</SermonTitle>
              <SermonDate>{sermon.date}</SermonDate>
            </CardContent>
          </SermonCard>
        ))}
      </SermonsGrid>

      <PaginationContainer>
        <PageButton
          onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
          disabled={currentPage === 1}
        >
          &lt;
        </PageButton>

        {(() => {
          const getPageNumbers = () => {
            const pages = [];
            const maxVisiblePages = 5;

            if (totalPages <= maxVisiblePages) {
              for (let i = 1; i <= totalPages; i++) pages.push(i);
            } else {
              // Always show first page
              pages.push(1);

              if (currentPage > 3) {
                pages.push('...');
              }

              // Show neighbors
              let start = Math.max(2, currentPage - 1);
              let end = Math.min(totalPages - 1, currentPage + 1);

              // Adjust if near start
              if (currentPage <= 3) {
                end = 4;
                start = 2;
              }
              // Adjust if near end
              if (currentPage >= totalPages - 2) {
                start = totalPages - 3;
                end = totalPages - 1;
              }

              // Clamp
              if (start <= 1) start = 2;
              if (end >= totalPages) end = totalPages - 1;

              for (let i = start; i <= end; i++) {
                pages.push(i);
              }

              if (currentPage < totalPages - 2) {
                pages.push('...');
              }

              // Always show last page
              pages.push(totalPages);
            }
            return pages;
          };

          return getPageNumbers().map((page, index) => (
            typeof page === 'number' ? (
              <PageButton
                key={page}
                active={currentPage === page}
                onClick={() => setCurrentPage(page)}
              >
                {page}
              </PageButton>
            ) : (
              <span key={`ellipsis-${index}`} style={{ display: 'flex', alignItems: 'end', padding: '0 5px' }}>...</span>
            )
          ));
        })()}

        <PageButton
          onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
          disabled={currentPage === totalPages}
        >
          &gt;
        </PageButton>
      </PaginationContainer>

      {selectedVideo && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.8)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 2000
        }} onClick={closeVideo}>
          <div style={{
            position: 'relative',
            width: '90%',
            maxWidth: '900px',
            aspectRatio: '16/9',
            background: '#000'
          }}>
            <iframe
              width="100%"
              height="100%"
              src={`https://www.youtube.com/embed/${selectedVideo}?autoplay=1`}
              title="YouTube video player"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            ></iframe>
            <button
              onClick={closeVideo}
              style={{
                position: 'absolute',
                top: '-40px',
                right: '-10px',
                background: 'none',
                border: 'none',
                color: '#fff',
                fontSize: '30px',
                cursor: 'pointer'
              }}
            >
              ✕
            </button>
          </div>
        </div>
      )}
    </SermonsContainer>
  );
};

export default Sermons; 